/**
 * requestId.ts — correlation ids for Deckorate backend calls.
 *
 * `request_id` is a non-secret correlation id echoed back on responses and SSE
 * frames for request/response tracing in logs. It is never used for auth or any
 * other security decision.
 *
 * Three strategies are tried in order, for browser compatibility:
 * `randomUUID()` is preferred; `getRandomValues()` covers browsers that support
 * Web Crypto but not `randomUUID()`; and, only when Web Crypto is entirely
 * unavailable, a timestamp + monotonic counter as a last resort. That last
 * fallback is NOT cryptographically secure and must never be used for anything
 * beyond a best-effort, non-secret correlation id — since the id needs no
 * randomness at all, it deliberately avoids `Math.random()` too.
 *
 * Extracted from speakerNotesApi.ts so every Deckorate feature mints ids the
 * same way rather than re-deriving this fallback chain per feature.
 */

const HEX_RADIX = 16;
const HEX_PAIR_WIDTH = 2;
const RANDOM_ID_BYTES = 16;
const TIMESTAMP_RADIX = 36;

/**
 * Counts calls that fell through to the last-resort id below. Module-scoped
 * (not per-request) purely so consecutive fallback ids stay unique for the
 * lifetime of this page/tab; it has no meaning outside that.
 */
let fallbackRequestSequence = 0;

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(HEX_RADIX).padStart(HEX_PAIR_WIDTH, "0")).join("");
}

/**
 * Mint a correlation id. `prefix` labels the non-UUID fallbacks so a log line
 * shows which feature produced the id (e.g. `"slide-transform-"`).
 */
export function newRequestId(prefix: string): string {
  const cryptoObj = globalThis.crypto;
  if (cryptoObj?.randomUUID) return cryptoObj.randomUUID();
  if (cryptoObj?.getRandomValues) {
    return `${prefix}${bytesToHex(cryptoObj.getRandomValues(new Uint8Array(RANDOM_ID_BYTES)))}`;
  }
  fallbackRequestSequence += 1;
  return `${prefix}${Date.now().toString(TIMESTAMP_RADIX)}-${fallbackRequestSequence}`;
}
